import { qk } from '@sunucupark/client-react';
import type { QueryClient } from '@tanstack/react-query';
import type { WebClient } from './client';

/**
 * Turns the gateway's hints into cache work (ADR 0008): an event never carries
 * data, so what changed is simply re-fetched. Returns the function that stops
 * listening again.
 */
export function connectRealtime(client: WebClient, queryClient: QueryClient): () => void {
  const unsubscribe = client.realtime.subscribe((event) => {
    switch (event.type) {
      case 'mailbox.changed': {
        const account = event.data.accountId;
        const kinds = new Set(event.data.kinds);
        if (kinds.has('folder') || kinds.has('message') || kinds.has('conversation')) {
          void queryClient.invalidateQueries({ queryKey: qk.folders(account) });
          void queryClient.invalidateQueries({ queryKey: qk.conversationLists(account) });
        }
        if (kinds.has('tag')) void queryClient.invalidateQueries({ queryKey: qk.tags(account) });
        if (kinds.has('contact'))
          void queryClient.invalidateQueries({ queryKey: qk.contactFolders(account) });
        if (kinds.has('appointment'))
          void queryClient.invalidateQueries({ queryKey: qk.calendars(account) });
        if (kinds.has('task'))
          void queryClient.invalidateQueries({ queryKey: ['account', account, 'tasks'] });
        return;
      }
      case 'session.revoked': {
        // The gateway ended this session: drop everything and let the router show sign-in.
        void client.session.end();
        queryClient.clear();
        return;
      }
      case 'reconnect':
        return;
    }
  });

  client.realtime.start();
  return () => {
    unsubscribe();
    void client.realtime.stop();
  };
}
